import { Tooltip } from '@app/components/various/Tooltip';
import { GetProductUrl } from '@app/logic/products';
import { cn } from '@app/utils/cn';
import { useCalculateWidthOverflowing } from '@app/utils/hooks/useCalculateWidthOverflowing';
import { useIsTouchScreen } from '@app/utils/hooks/useIsTouchScreen';
import { useOffsetWidth } from '@app/utils/hooks/useOffsetWidth';
import { isDefined } from '@app/utils/is';
import { Product } from '@typings';
import React from 'react';
import { Link } from 'react-router-dom';

import { ProductItemContextProvider, useProductItemContext } from '../context/ProductItemContext';
import { CmsProductButtons } from '../ProductCard/CmsProductButtons';
import { Gallery, GalleryDots } from '../ProductCard/Gallery';
import { ProductPrice } from '../ProductPrice';

import { CmsProductCardAttributes } from './CmsProductCardAttributes';

const COMPACT_CARD_WIDTH = 240;
const CARD_TRANSLATE_OFFSET = 48;
const DOTS_HEIGHT = 24;
const BADGES_PADDING = 24;

export interface Props extends ComponentProps {
  productInfo: Product.Standard;
  getProductUrl: GetProductUrl;
}

export interface ComponentProps {
  className?: string;
  style?: React.CSSProperties;
  isInvalid?: boolean;
  isInPreview?: boolean;
  isInEditor?: boolean;
  hideButtons?: boolean;
}

export const CmsProductComponent = ({ style, className, isInEditor, isInPreview, isInvalid, hideButtons = false }: ComponentProps) => {
  const {
    containerRef,
    activeSlideVariant,
    isProductInOrder,
    productPathName,
    isMutating,
    activeSlideIndex,
    gallerySlides,
    setActiveSlideIndex,
  } = useProductItemContext();
  const nameRef = React.useRef<HTMLHeadingElement>(null);
  const isTouchScreen = useIsTouchScreen();
  const width = useOffsetWidth(containerRef);
  const isNameOverflowing = useCalculateWidthOverflowing({ ref: nameRef, widthToReact: width });
  const isCompact = width < COMPACT_CARD_WIDTH;
  const showButtons = !hideButtons && !isInEditor;
  const shouldExpandByDefault = !isTouchScreen && showButtons;

  return (
    <div
      ref={containerRef}
      className={cn(
        'group relative flex h-full flex-col overflow-hidden rounded-regular bg-white pb-3',
        {
          'opacity-40': isInvalid && isInEditor,
        },
        className,
      )}
      style={style}
      data-testid="productCard"
      {...(isInEditor ? { inert: '' } : undefined)}
    >
      <Gallery
        isCompact={isCompact}
        overlapHeight={isTouchScreen ? CARD_TRANSLATE_OFFSET + DOTS_HEIGHT : DOTS_HEIGHT}
        parentWidth={width}
        isDisabled={isInPreview}
      />
      <div
        className={cn('flex flex-1 flex-col bg-white px-3', {
          '!-translate-y-12': !isTouchScreen && showButtons && (isProductInOrder || isMutating),
          'transition-transform group-focus-within:-translate-y-12 group-focus-within:transition-none group-hover:-translate-y-12':
            shouldExpandByDefault,
        })}
      >
        <div className="relative flex flex-1 flex-col">
          {isDefined(activeSlideVariant) && (
            <div className={cn('relative flex flex-1 flex-col justify-between pt-5', { '-mb-12': shouldExpandByDefault })}>
              <div className="absolute -top-3 left-1/2 max-w-full -translate-x-1/2 rounded-large bg-white">
                <GalleryDots
                  activeSlideIndex={activeSlideIndex}
                  gallerySlides={gallerySlides}
                  setActiveSlideIndex={setActiveSlideIndex}
                  parentWidth={width - BADGES_PADDING}
                />
              </div>

              <div className="flex flex-1 flex-col" {...(isInPreview ? { inert: '' } : undefined)}>
                <Link className="mb-2 inline rounded-small focus-visible:focus-outline" to={productPathName}>
                  <Tooltip overlayClassName="max-w-52" content={isNameOverflowing && activeSlideVariant.name} disableOnTouchScreen>
                    <h3 ref={nameRef} className="mb-1 line-clamp-2 heading-3">
                      {activeSlideVariant.name}
                    </h3>
                  </Tooltip>
                  <ProductPrice isCompact={isCompact} productInfo={activeSlideVariant} parentWidth={width} />
                </Link>
                <div className="pb-3">
                  <CmsProductCardAttributes variant={activeSlideVariant} />
                </div>
              </div>

              {showButtons && (
                <div
                  className={cn('transition-opacity', {
                    'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100': !isTouchScreen && !isProductInOrder && !isMutating,
                  })}
                  {...(isInPreview ? { inert: '' } : undefined)}
                >
                  <CmsProductButtons isCompact={isCompact} product={activeSlideVariant} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const CmsProduct = React.memo(
  ({ getProductUrl, productInfo, className, style, isInPreview, isInEditor, isInvalid, hideButtons }: Props) => {
    return (
      <ProductItemContextProvider productInfo={productInfo} getProductUrl={getProductUrl}>
        <CmsProductComponent
          className={className}
          style={style}
          isInPreview={isInPreview}
          isInEditor={isInEditor}
          isInvalid={isInvalid}
          hideButtons={hideButtons}
        />
      </ProductItemContextProvider>
    );
  },
);
